import type {
  ConflictReport,
  ProductWithLines,
  PushPlan,
  PushResult,
  ResolveBody,
  SyncLogEntry,
  ValidationResult,
} from '@klingerator/shared';
import { api } from './api';

export interface RefreshResult {
  product: ProductWithLines;
  report: ConflictReport | null;
  conflict: boolean;
}

export interface ResolveResult {
  product: ProductWithLines;
  applied: unknown;
}

const noToast = { noToast: true } as const;

/**
 * Typed wrappers rond de sync-endpoints per product (`/api/products/:id/…`).
 * Fouten worden NIET automatisch getoast (noToast) — de sync-store vertaalt de foutcodes
 * (VALIDATION_FAILED, UNRESOLVED_CONFLICT, NO_TEMPLATE, PUSH_PROD_ADMIN_ONLY) naar UI-acties.
 */
export const syncService = {
  /** Kan 10-30 s duren (Unit4-checks). */
  async validate(id: number): Promise<ValidationResult> {
    const { data } = await api.post<ValidationResult>(`/products/${id}/validate`, undefined, noToast);
    return data;
  },

  /** 422 VALIDATION_FAILED (details.issues), 409 UNRESOLVED_CONFLICT, 400 NO_TEMPLATE, 403 PUSH_PROD_ADMIN_ONLY */
  async dryRun(id: number): Promise<PushPlan> {
    const { data } = await api.post<PushPlan>(`/products/${id}/push`, undefined, {
      params: { dry_run: 'true' },
      ...noToast,
    });
    return data;
  },

  /** 200 bij succes; 502 bij gedeeltelijk mislukt — body is dan óók een PushResult (ok:false). */
  async push(id: number): Promise<PushResult> {
    const { data } = await api.post<PushResult>(`/products/${id}/push`, { confirm: true }, noToast);
    return data;
  },

  async resume(id: number): Promise<PushResult> {
    const { data } = await api.post<PushResult>(`/products/${id}/push/resume`, undefined, noToast);
    return data;
  },

  async refresh(id: number): Promise<RefreshResult> {
    const { data } = await api.post<RefreshResult>(`/products/${id}/refresh`, undefined, noToast);
    return data;
  },

  async conflicts(id: number): Promise<ConflictReport | null> {
    const { data } = await api.get<{ report: ConflictReport | null }>(`/products/${id}/conflicts`, {
      silent: true,
      ...noToast,
    });
    return data.report;
  },

  /** 400 CHOICES_REQUIRED (details.keys) als both_changed-regels geen keuze hebben. */
  async resolve(id: number, body: ResolveBody): Promise<ResolveResult> {
    const { data } = await api.post<ResolveResult>(`/products/${id}/resolve`, body, noToast);
    return data;
  },

  async productSyncLogEntry(id: number, logId: number): Promise<SyncLogEntry> {
    const { data } = await api.get<SyncLogEntry>(`/products/${id}/sync-log/${logId}`);
    return data;
  },
  async productSyncLog(
    id: number,
    params: { limit?: number; offset?: number } = {},
  ): Promise<{ items: SyncLogEntry[]; total: number }> {
    const { data } = await api.get<{ items: SyncLogEntry[]; total: number }>(`/products/${id}/sync-log`, {
      params,
      silent: true,
      ...noToast,
    });
    return data;
  },
};

/** Haalt een PushResult uit een 502-fout (gedeeltelijk mislukte push), of null. */
export function pushResultFromError(err: unknown): PushResult | null {
  const data = (err as any)?.response?.data;
  if (data && typeof data === 'object' && 'plan' in data && 'ok' in data) return data as PushResult;
  return null;
}
